import React from 'react';
import { Instagram, Facebook, PawPrint, ArrowUpRight } from 'lucide-react';
import Reveal from '../ui/Reveal';

// Follow-us cards. Each one is a link as well as a QR, because a QR is no use
// to someone already reading this on their phone - they cannot scan their own
// screen. Tap on a phone, scan from a laptop or a printout.

const CHANNELS = [
  {
    name: 'Instagram',
    handle: '@bubblesbarkorlando',
    href: 'https://www.instagram.com/bubblesbarkorlando',
    qr: '/qr-instagram.svg',
    icon: Instagram,
    blurb: 'Fresh cuts, fluffy tails and the odd bakery close-up.',
    ring: 'group-hover:ring-pink-300',
    tint: 'from-pink-500 to-rose-400',
  },
  {
    name: 'Facebook',
    handle: 'Bubbles & Bark',
    href: 'https://www.facebook.com/share/1ELsCVeRfw/',
    qr: '/qr-facebook.svg',
    icon: Facebook,
    blurb: 'Opening hours, news and photos from the shop.',
    ring: 'group-hover:ring-cyan-300',
    tint: 'from-cyan-500 to-blue-400',
  },
];

// Scattered paws, purely decorative.
const PAWS = [
  { className: 'left-[6%] top-12', size: 'w-9 h-9', rotate: -22, opacity: 'opacity-[0.13]' },
  { className: 'left-[16%] bottom-16', size: 'w-6 h-6', rotate: 14, opacity: 'opacity-[0.10]' },
  { className: 'right-[8%] top-24', size: 'w-11 h-11', rotate: 28, opacity: 'opacity-[0.12]' },
  { className: 'right-[18%] bottom-10', size: 'w-7 h-7', rotate: -12, opacity: 'opacity-[0.10]' },
];

export default function Social() {
  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-white via-pink-50/60 to-white overflow-hidden">
      {PAWS.map((p, i) => (
        <PawPrint
          key={i}
          aria-hidden
          style={{ transform: `rotate(${p.rotate}deg)` }}
          className={`pointer-events-none absolute text-pink-500 ${p.className} ${p.size} ${p.opacity} hidden sm:block`}
        />
      ))}

      <div className="relative max-w-5xl mx-auto px-4">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs md:text-sm font-extrabold uppercase tracking-[0.22em] text-pink-500 flex items-center justify-center gap-2">
            <PawPrint className="w-4 h-4" /> Follow along
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-black text-ink-900 leading-[1.02] tracking-tight">
            See every
            <span className="italic bg-gradient-to-r from-pink-500 to-cyan-500 bg-clip-text text-transparent"> happy tail.</span>
          </h2>
          <p className="mt-4 text-gray-600 text-base md:text-lg">
            Point your camera at a code, or tap it on your phone.
          </p>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-6 md:gap-8">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.12}>
              <a
                href={c.href}
                target="_blank"
                rel="noreferrer"
                className={`group h-full flex flex-col items-center text-center bg-white rounded-[2rem] border border-pink-100 shadow-xl shadow-pink-100/60 ring-2 ring-transparent ${c.ring} transition-all duration-300 hover:-translate-y-1.5 p-7 md:p-8`}
              >
                <span className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${c.tint} shadow-lg`}>
                  <c.icon className="w-6 h-6 text-white" />
                </span>

                <h3 className="mt-4 font-display text-2xl font-black text-ink-900">{c.name}</h3>
                <p className="mt-1 text-sm font-bold text-pink-600">{c.handle}</p>
                <p className="mt-2 text-sm text-gray-600 max-w-[26ch]">{c.blurb}</p>

                <img
                  src={c.qr}
                  alt={`QR code linking to Bubbles & Bark on ${c.name}`}
                  width="200"
                  height="200"
                  loading="lazy"
                  className="mt-6 w-44 h-44 md:w-48 md:h-48 rounded-2xl group-hover:scale-[1.03] transition-transform duration-300"
                />

                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-extrabold text-ink-900 group-hover:text-pink-600 transition-colors">
                  Scan or tap to follow
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
